import React, { useState, useEffect, forwardRef, useImperativeHandle, useContext } from "react"
import { toast } from "react-toastify"
import { useGetCall, useDelete, fetchPost } from "utils/api"
import Loading from "helpers/Loading"
import { formatISODate } from "utils/date"
import { showResponseMessage } from "utils/message";
import { LoadingContext } from "helpers/LoadingContext"
import { usePagination, useCount } from "utils/gql";
import { downloadCSV, downloadExcel } from "utils/files";
import View from "components/crh/employees/List.view"

const EmployeesList = forwardRef((props, ref) => {

	const {
		user,
		company,
		theme,
		countRef,
		refresh,
		searchFilters,
		addEditEmployeeModal,
		addLeaveEmployeeModal
	} = props

	const userType = sessionStorage.getItem("type")
	const { loading, setLoading } = useContext(LoadingContext);
	const [refreshValue, setRefreshValue] = useState(null)
	const [employees, setEmployees] = useState([])
	const [exportFormat, setExportFormat] = useState("csv")
	const [exportData, setExportData] = useState(null)

	const pageSize = 50;
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);

	if (refresh !== refreshValue)
		setRefreshValue(refresh)

	const queryParams = {
		order: [
      ["pin", "ASC"]
    ],
    where: {
			...(
				searchFilters.payroll_id 
					? { payroll_id: { eq: searchFilters.payroll_id } }
					: {}
			),
			...(
				searchFilters.office_id
					? { office_id: { eq: searchFilters.office_id } }
					: {}
			),
			...(
				searchFilters.job_id
					? { job_id: { eq: searchFilters.job_id } }
					: {}
			),
			...(
				searchFilters.department_id
					? { department_id: { eq: searchFilters.department_id } }
					: {}
			),
			...(
				searchFilters.shift_id
					? { shift_id: { eq: searchFilters.shift_id } }
					: {}
			),
			...(
				searchFilters.status
					? { status: { eq: searchFilters.status } }
					: {}
			),
			...(
				searchFilters.geolocation
					? { geolocation: { eq: searchFilters.geolocation } }
					: {}
			),
			...(
				searchFilters.search 
					? {
							ilike_concat: {
								fields: [
									"pin",
									"Employee.key",
									"firstname",
									"dad_lastname",
									"mom_lastname",
									"email"
								],
								value: searchFilters.search
							}
						}
					: {}
			),
			register_crh: {
				eq: true
			}
    }
	}

	const reqEmployees = usePagination(`
    Employee {
			totalPages
      result {
        id
				key
				pin
				firstname
				dad_lastname
				mom_lastname
				email
				job {
					id
					description
				}
				department {
					id
					description
				}
      }
    }
  `, page, pageSize, queryParams,
	{
		onCompleted: (response) => {
			setLoading(false);
			setEmployees(response.result.sort((a, b) => {
				const keyA = parseInt(a.key, 10);
				const keyB = parseInt(b.key, 10);
				if (keyA < keyB) return -1;
				if (keyA > keyB) return 1;
				return 0;
			}));
			setTotalPages(response.totalPages);
			if(page > response.totalPages) setPage(1);
		},
		onError: (error) => {
			setLoading(false);
			showResponseMessage(error.status, "incidence_requests", "solicitudes de incidencias", "read");
		}
	});

	const reqCount = useCount(`
		Employee {
			count
      result { 
				id
			}
	}`, queryParams, {
		onCompleted: (response) => {
			countRef.current.innerHTML = response?.count || 0;
		}
	});

	const [callEmployees] = useGetCall("/employees", {
		onCompleted: (data) => {

			const employees = data?.data || [];

			let csv = [
				[
					"ID Empleado",
					"ID Lector",
					"Nombre",
					"Apellido paterno",
					"Apellido materno",
					"CURP",
					"RFC",
					"NSS",
					"Salario",
					"Fecha de ingreso",
					"Fecha inicia turno",
					"Correo",
					"Sucursal",
					"Departamento",
					"Puesto",
					"Grupo",
					"Contrato",
					"Tipo de nómina",
					"Jefe directo",
					"Área",
					"Subárea",
					"Turno",
					"Centro de costo",
					"Empresa de nómina",
					"Status",
					"Fecha de baja",
					"Motivo de baja",
					"Comentarios de baja",
				]
			];

			employees.forEach((employee) => {
				csv.push([
					employee.key,
					employee.pin,
					employee.firstname,
					employee.dad_lastname,
					employee.mom_lastname ? employee.mom_lastname : "",
					employee.curp ? employee.curp : "",
					employee.rfc ? employee.rfc : "",
					employee.nss ? employee.nss : "",
					employee.salary ? employee.salary : "",
					employee.admission ? formatISODate(employee.admission) : "",
					employee.start_shift ? formatISODate(employee.start_shift) : "",
					employee.email ? employee.email : "",
					employee.office ? employee.office.description : "",
					employee.department ? employee.department.description : "",
					employee.job ? employee.job.description : "",
					employee.group ? employee.group.description : "",
					employee.contract ? employee.contract.description : "",
					employee.payroll ? employee.payroll.description : "",
					employee.boss ? employee.boss.description : "",
					employee.area ? employee.area.description : "",
					employee.subarea ? employee.subarea.description : "",
					employee.shift ? employee.shift.description : "",
					employee.cost_center ? employee.cost_center.description : "",
					employee.company_payroll ? employee.company_payroll : "",
					employee.status == "ACTIVE" ? "Activo" : "Inactivo",
					employee.leave ? formatISODate(employee.leave) : "",
					employee.reason_leave?.description??"",
					employee.leave_comments??""
				])
			});

			setExportData(csv);

		},
		onError: (error) => {
			setLoading(false);
			showResponseMessage(error.status, "employees", "empleados", "read")
		}
	})

	const [callDelete, reqDelete] = useDelete("/employees", {
		onCompleted: () => {
			if (!toast.isActive("delete-device"))
				toast.success("Empleado eliminado", { toastId: "delete-employees" })
			reqEmployees.refetch()
			setRefreshValue(Math.random())
		},
		onError: (error) => {
			console.error(error)
			showResponseMessage(error.status, "employees", "empleados", "delete")
		}
	})


	useImperativeHandle(ref, () => ({
		downloadEmployeeExcel(format) {
			return downloadEmployeeExcel(format)
		}
	}))

	const downloadEmployeeExcel = (format) => {
		setExportFormat(format);
		callEmployees({
			...searchFilters,
			register_crh: true,
			company
		});
	}

	const onDelete = (id) => {
		fetchPost("/employees/check_permission", { type: "delete" }).then((response) => {
			if(response.data) {
				const data = response.data;
				if (data.isAllowed === true) {
					if (window.confirm("¿Estás seguro de eliminar el empleado?")) {
						callDelete({ id: id })
					}
				} else {
					if (!toast.isActive("toast-delete-empleado-unauthorized")) {
						toast.error("Error, no tienes los permisos para eliminar empleados", { toastId: "toast-delete-empleado-unauthorized" })
					}
				}
			}
		})
	}

	useEffect(() => {
		setLoading(true);
		// callEmployees({ ...searchFilters, register_crh: true, company });
		reqEmployees.refetch();
	}, [refreshValue, searchFilters])

	useEffect(() => {

		if(!exportData) return;
		if(!exportFormat) return;

		if(exportFormat === "csv") {
			downloadCSV({
				user,
				cells: exportData,
				report: "empleados",
				header: false
			});
		}
		else if(exportFormat === "xlsx") {
			downloadExcel({
				user,
				cells: exportData,
				report: "empleados",
				header: false
			});
		}

		setExportData(null);
		setExportFormat(null);

	}, [exportData, exportFormat, user])

	const deleteLoading = reqDelete.isLoading

	const onEdit = (id) => addEditEmployeeModal(id)
	if (loading) return <Loading />

	return (
		<View
			page={page}
			setPage={setPage}
			totalPages={totalPages}
			deleteLoading={deleteLoading}
			employees={employees}
			onEdit={onEdit}
			onDelete={onDelete}
			theme={theme}
			userType={userType}
			addLeaveEmployeeModal={addLeaveEmployeeModal}
		/>
	)

})

export default EmployeesList